import React, {useReducer, useRef} from 'react'

import LoginActions from '../../../../actions/LoginActions.js'
import reducer from '../../../../helpers/reducer.js'

import {Alert, H3, H5, S2, P, Colors, IconButton, Toast, Button} from '../../../../components/UI/index.js'
import {formSpacing, FormRow, FormStack, MetricInput, Toggle, Input, Select} from '../../../../components/UI/forms/index.js'
import t from '../../../../helpers/Languages.js'

const initialState = {
    loading: false,
    verified: false,
    error: ''
}

export default function ShopifySettings({user, onChange, location}) {
    const [{loading, verified, error}, dispatch] = useReducer(reducer, initialState)
    const toastRef = useRef(null)

    function onChangeChecked(key, event) {
        user.settings.webshops.shopify[key] = event.target.checked

        onChange(user)
    }

    const onChangeValue = (key, event, parser) => {
        user.settings.webshops.shopify[key] = parser ? parser(event.target.value) : event.target.value

        onChange(user)
    }

    function onChangeValueParcel(key, event) {
        user.settings.parcels[key] = event.target.value

        onChange(user)
    }

    function onChangeCheckedParcel(key, event) {
        user.settings.parcels[key] = event.target.checked

        onChange(user)
    }

    function onVerifyConnection() {
        dispatch({loading: true, verified: false, error: ''})

        onChange(user, true)

        setTimeout(() => { // wait for saving
            LoginActions.verifyWebshop('shopify', (err) => {
                if (err) {
                    dispatch({loading: false, error: `Verificatie niet succesvol, controleer de ingevulde gegevens. Foutmelding: ${err}`})
                } else {
                    dispatch({loading: false, verified: true})
                }
            })
        }, 1000)
    }

    return (
        <div>
            {location.state && location.state.message &&
                <Alert style={{marginBottom: 6}} variant='success'>{location.state.message}</Alert>
            }
            {location.state && location.state.err &&
                <Alert style={{marginBottom: 6}} variant='error'>{`Shopify foutmelding: ${location.state.err}`}</Alert>
            }

            <H3 style={{marginBottom: 36}}>Shopify</H3>

            <H5 style={{marginBottom: 24}}>Installatie instructies</H5>

            <P>Stap 1</P>
            <S2 style={{width: 700, marginBottom: 12}}>
                Ga naar de Shopify App Store en zoek op &quot;Veloyd&quot;. Klik op &quot;Add app&quot; en vervolgens op &quot;Installeer App&quot;
            </S2>

            <P>Stap 2</P>
            <S2 style={{width: 700, marginBottom: 12}}>
                Je wordt nu doorverwezen naar de inlog pagina van Veloyd. Log in met je emailadres en wachtwoord.
            </S2>

            <P>Stap 3</P>
            <S2 style={{width: 700}}>
                De koppeling is nu actief. Je wordt automatisch doorgestuurd naar de instellingen pagina, hier kun je nog enkele opties aanpassen.
            </S2>

            <H5 style={{marginBottom: 24, marginTop: 36}}>Instellingen</H5>

            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <Toggle
                    label='Actief'
                    checked={user.settings.webshops.shopify.active}
                    onChange={(event) => onChangeChecked('active', event)}
                />
            </FormStack>

            {user.settings.webshops.shopify.active &&
                <>
                    <FormStack style={{marginBottom: formSpacing.rowGap}}>
                        <Input
                            label='Webshop URL'
                            disabled
                            value={user.settings.webshops.shopify.url}
                            onChange={(event) => onChangeValue('url', event)}
                        />

                        <Input
                            label='API key'
                            disabled
                            value={user.settings.webshops.shopify.apiKey}
                            onChange={(event) => onChangeValue('apiKey', event)}
                        />

                        <Input
                            type='number'
                            min={1}
                            max={90}
                            label='Aantal dagen terugkijken voor ophalen bestellingen'
                            value={user.settings.webshops.shopify.nrOfDays || 7}
                            onChange={(event) => onChangeValue('nrOfDays', event, parseInt)}
                        />

                        <Select
                            label='Update bestelling status in Shopify'
                            info=''
                            value={user.settings.webshops.shopify.updateStatus}
                            onChange={(event) => onChangeValue('updateStatus', event)}
                        >
                            <option value=''>Nooit</option>
                            <option value='confirm'>Na bevestigen</option>
                            <option value='afterSent'>Na verzenden</option>
                        </Select>

                        <Toggle
                            label='Alleen betaalde bestellingen importeren'
                            checked={user.settings.webshops.shopify.ignoreNotPayedOrders}
                            onChange={(event) => onChangeChecked('ignoreNotPayedOrders', event)}
                        />

                        <Toggle
                            label='Automatisch brievenbuspakket'
                            info='Zendingen die aan de ingestelde voorwaarden voldoen worden automatisch aangemeld als brievenbuspakket.'
                            checked={!!user.settings.parcels.autoMailboxParcel}
                            onChange={(event) => onChangeCheckedParcel('autoMailboxParcel', event)}
                        />

                        {user.settings.parcels.autoMailboxParcel &&
                            <>
                                <FormRow>
                                    <Toggle
                                        label='Maximaal gewicht'
                                        checked={!!user.settings.parcels.useMailboxParcelWeight}
                                        onChange={(event) => onChangeCheckedParcel('useMailboxParcelWeight', event)}
                                    />

                                    <MetricInput
                                        label='Gewicht'
                                        value={user.settings.parcels.mailboxParcelWeight}
                                        onChange={(event) => onChangeValueParcel('mailboxParcelWeight', event)}
                                        placeholder={2000}
                                        max={2000}
                                        append='g'
                                        disabled={!user.settings.parcels.useMailboxParcelWeight}
                                    />
                                </FormRow>

                                <FormRow>
                                    <Toggle
                                        label='Maximale waarde'
                                        checked={!!user.settings.parcels.useMailboxParcelValue}
                                        onChange={(event) => onChangeCheckedParcel('useMailboxParcelValue', event)}
                                    />

                                    <MetricInput
                                        label='Waarde'
                                        value={user.settings.parcels.mailboxParcelValue}
                                        onChange={(event) => onChangeValueParcel('mailboxParcelValue', event)}
                                        divisionFactor={100}
                                        prepend='€'
                                        disabled={!user.settings.parcels.useMailboxParcelValue}
                                    />
                                </FormRow>
                            </>
                        }
                    </FormStack>

                    <H5 style={{marginBottom: 24, marginTop: 36}}>{t('webhookUrl')}</H5>

                    <P>{t('webhookUrlExplanation')} {t('seeThe')} <a target='_blank' href='https://handleiding.veloyd.nl/docs/shopify/' rel='noreferrer'>{t('manualLowercase')}</a> {t('forMoreInfoOnWebhooks')}</P>

                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <pre style={{color: Colors.textDark}}>{`https://${window.location.host}/api/parcel/shopify?customer_id=${user._id}`}</pre>
                        <IconButton
                            onClick={() => {
                                navigator.clipboard.writeText(`https://${window.location.host}/api/parcel/shopify?customer_id=${user._id}`).then(() => {
                                    toastRef.current?.showToast('success', 'Link gekopieerd', 2500)
                                })
                            }}
                            icon='mdi mdi-content-copy'
                        />
                    </div>

                    <FormStack>
                        <Button
                            variant='text'
                            loading={loading}
                            onClick={onVerifyConnection}
                        >
                            Verifieer
                        </Button>

                        {error && (
                            <Alert style={{marginBottom: 6}} onClose={() => dispatch({error: ''})} variant='error'>{error}</Alert>
                        )}

                        {!error && verified && (
                            <Alert style={{marginBottom: 6}} onClose={() => dispatch({verified: false})}>Verificatie succesvol.</Alert>
                        )}
                    </FormStack>
                </>
            }

            <Toast ref={toastRef} />
        </div>
    )
}
